import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { AuthGuard } from './guards/auth.guard';
import { AdminGuard } from './guards/admin.guard';
import { SopViewerComponent } from './components/sop-posts/sop-viewer.component';
import { PolicyViewerComponent } from './components/policy-viewer/policy-viewer.component';
import { map } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { DocumentsComponent } from './components/documents/documents.component';
import { VacationCalendarComponent } from './components/vacation-calendar/vacation-calendar.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadComponent: () => import('./components/login/login.component')
      .then(m => m.LoginComponent),
    canActivate: [() => {
      const authService = inject(AuthService);
      return authService.user$.pipe(
        map(user => {
          if (user) {
            window.location.href = '/dashboard';
            return false;
          }
          return true;
        })
      );
    }]
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./components/dashboard/dashboard.component')
      .then(m => m.DashboardComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'profile',
    loadComponent: () => import('./components/profile/profile.component')
      .then(m => m.ProfileComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'time-tracking',
    loadComponent: () => import('./components/time-tracking/time-tracking.component')
      .then(m => m.TimeTrackingComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'time-off',
    loadComponent: () => import('./components/time-off/time-off-request.component')
      .then(m => m.TimeOffRequestComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'documents',
    component: DocumentsComponent,
    canActivate: [AuthGuard],
    title: 'Documents'
  },
  {
    path: 'sop',
    loadComponent: () => import('./components/sop-posts/sop-posts.component')
      .then(m => m.SopPostsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'sop/:slug',
    component: SopViewerComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'policies',
    loadComponent: () => import('./components/policy-posts/policy-posts.component')
      .then(m => m.PolicyPostsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'policy/:slug',
    component: PolicyViewerComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'admin',
    children: [
      {
        path: 'init',
        loadComponent: () => import('./admin/components/admin-init/admin-init.component')
          .then(m => m.AdminInitComponent),
        canActivate: [AuthGuard, AdminGuard]
      },
      {
        path: 'time-reports',
        children: [
          {
            path: '',
            loadComponent: () => import('./admin/components/time-reports/time-reports.component')
              .then(m => m.TimeReportsComponent),
            canActivate: [AuthGuard, AdminGuard]
          },
          {
            path: 'calendar',
            loadComponent: () => import('./admin/components/time-calendar/time-calendar.component')
              .then(m => m.TimeCalendarComponent),
            canActivate: [AuthGuard, AdminGuard]
          }
        ]
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            loadComponent: () => import('./admin/components/users/users.component')
              .then(m => m.UsersComponent),
            canActivate: [AuthGuard, AdminGuard]
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./admin/components/users/edit-user')
              .then(m => m.EditUserComponent),
            canActivate: [AuthGuard, AdminGuard]
          }
        ]
      },
      {
        path: 'time-off-approval',
        loadComponent: () => import('./admin/components/time-off-approval/time-off-approval.component')
          .then(m => m.TimeOffApprovalComponent),
        canActivate: [AuthGuard, AdminGuard]
      },
      {
        path: 'categories',
        loadComponent: () => import('./admin/components/categories/categories.component')
          .then(m => m.CategoriesComponent),
        canActivate: [AuthGuard, AdminGuard]
      },
      {
        path: 'global-settings',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./admin/components/global-settings/global-settings.component')
              .then(m => m.GlobalSettingsComponent),
            canActivate: [AuthGuard, AdminGuard],
            title: 'Global Settings'
          },
          {
            path: 'emails',
            loadComponent: () =>
              import('./admin/components/email-settings/email-settings.component')
              .then(m => m.EmailSettingsComponent),
            canActivate: [AuthGuard, AdminGuard],
            title: 'Email Settings'
          },
          {
            path: 'logs',
            loadComponent: () =>
              import('./admin/components/logs/logs.component')
              .then(m => m.LogsComponent),
            canActivate: [AuthGuard, AdminGuard],
            title: 'System Logs'
          }
        ]
      },
      {
        path: 'sops',
        children: [
          {
            path: 'drafts',
            loadComponent: () => import('./admin/components/sop-form/sop-drafts.component')
              .then(m => m.SopDraftsComponent),
            canActivate: [AuthGuard, AdminGuard]
          },
          {
            path: 'create',
            loadComponent: () => import('./admin/components/sop-form/sop-form.component')
              .then(m => m.SopFormComponent),
            canActivate: [AuthGuard, AdminGuard]
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./admin/components/sop-form/sop-form.component')
              .then(m => m.SopFormComponent),
            canActivate: [AuthGuard, AdminGuard]
          }
        ]
      },
      {
        path: 'policies',
        children: [
          {
            path: 'drafts',
            loadComponent: () => import('./admin/components/policy-form/policy-drafts.component')
              .then(m => m.PolicyDraftsComponent),
            canActivate: [AuthGuard, AdminGuard]
          },
          {
            path: 'create',
            loadComponent: () => import('./admin/components/policy-form/policy-form.component')
              .then(m => m.PolicyFormComponent),
            canActivate: [AuthGuard, AdminGuard]
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./admin/components/policy-form/policy-form.component')
              .then(m => m.PolicyFormComponent),
            canActivate: [AuthGuard, AdminGuard]
          }
        ]
      }
    ]
  },
  {
    path: 'not-found',
    loadComponent: () => import('./components/not-found/not-found.component')
      .then(m => m.NotFoundComponent)
  },
  {
    path: 'schedule-settings',
    loadComponent: () => import('./components/work-schedule/work-schedule.component')
      .then(m => m.WorkScheduleComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'vacation-calendar',
    component: VacationCalendarComponent,
    canActivate: [AuthGuard],
    title: 'Vacation Calendar'
  },
  {
    path: '**',
    redirectTo: '/not-found'
  }
];
